/**
 * Realtime Notifications Store
 * Intelligent setTimeout-like system for MQTT notifications
 * Just schedules and emits events, doesn't decide what to do
 */

import eventBusConstants from '@/domain/core/constants/eventBus'

const state = {
  // Map of scheduled notifications by gallery ID: { galleryId: { notificationData, readyAt } }
  scheduledNotifications: {}
}

export const getters = {
  // Returns all scheduled notifications (without time check)
  scheduledNotifications: state => state.scheduledNotifications,
  
  // Returns true if there are any scheduled notifications (not ready yet)
  hasScheduledNotifications: state => {
    const now = Date.now()
    return Object.values(state.scheduledNotifications).some(notification => notification.readyAt > now)
  }
}

export const mutations = {
  // Schedule or update a notification for a specific gallery
  scheduleNotification(state, { galleryId, notificationData, refreshTime }) {
    const now = Date.now()
    const readyAt = now + (refreshTime * 1000)
    
    // If we already have a notification for this gallery, update data but keep original timing
    if (state.scheduledNotifications[galleryId]) {
      state.scheduledNotifications[galleryId] = {
        ...state.scheduledNotifications[galleryId],
        notificationData: { ...state.scheduledNotifications[galleryId].notificationData, ...notificationData }
        // Keep the original readyAt time - don't reset timer!
      }
    } else {
      // New notification - set timer
      state.scheduledNotifications[galleryId] = {
        notificationData,
        readyAt,
        refreshTime
      }
    }
  },
  
  // Remove a scheduled notification
  removeScheduledNotification(state, galleryId) {
    delete state.scheduledNotifications[galleryId]
  },
  
  // Clear all scheduled notifications
  clearAllScheduledNotifications(state) {
    state.scheduledNotifications = {}
  }
}

export const actions = {
  // Schedule a notification (setTimeout-like behavior)
  scheduleNotification({ commit }, { galleryId, notificationData, refreshTime }) {
    // If refresh time is 0, ignore the notification
    if (refreshTime === 0) {
      return
    }
    
    commit('scheduleNotification', { galleryId, notificationData, refreshTime })
  },
  
  // Called periodically to emit ready notifications
  async emitReadyNotifications({ getters, commit, dispatch }) {
    const now = Date.now()
    const scheduledNotifications = getters.scheduledNotifications
    const readyNotifications = []
    
    // Find ready notifications (do the time check here, not in getter)
    Object.entries(scheduledNotifications).forEach(([galleryId, notification]) => {
      const isReady = notification.readyAt <= now
      
      if (isReady) {
        readyNotifications.push({
          galleryId,
          ...notification
        })
      }
    })
    
    if (readyNotifications.length === 0) {
      return
    }
    
    // Process all ready notifications
    for (const notification of readyNotifications) {
      try {
        
        // Emit the notification - let the receiver decide what to do
        await dispatch('eventBus/emitEvent', {
          event: eventBusConstants.realtimeNotifications.notification_ready,
          payload: {
            galleryId: notification.galleryId,
            notificationData: notification.notificationData
          }
        }, { root: true })
        
        // Remove the processed notification
        commit('removeScheduledNotification', notification.galleryId)
        
      } catch (error) {
        console.error('Error emitting ready notification:', error)
        // Remove the failed notification to prevent infinite loops
        commit('removeScheduledNotification', notification.galleryId)
      }
    }
  },
  
  // Cleanup all scheduled notifications
  cleanup({ commit }) {
    commit('clearAllScheduledNotifications')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}